import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createInstallmentPlanThunk } from "../../../store/thunks/branchInstallmentThunks";
import {
  selectIsSavingPlan,
  selectValidationErrors,
} from "../../../store/selectors/branchInstallmentSelectors";
import { XIcon } from "@heroicons/react/outline";

const CreateInstallmentPlanModal = ({ branchCode, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector(selectIsSavingPlan);
  const validationErrors = useSelector(selectValidationErrors);

  const [formData, setFormData] = useState({
    planName: "",
    tenureMonths: 6,
    minOrderValue: 10000,
    maxOrderValue: 500000,
    downPayment: {
      required: true,
      minPercentage: 20,
      minAmount: 0,
    },
    interestRate: {
      annual: 12,
    },
    processingFee: {
      amount: 1,
      type: "PERCENTAGE",
      charged: "UPFRONT",
    },
    requiresPremiumCustomer: false,
    requiresGuarantor: false,
    benefits: [],
  });

  const [benefitInput, setBenefitInput] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleAddBenefit = () => {
    if (benefitInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        benefits: [...prev.benefits, benefitInput.trim()],
      }));
      setBenefitInput("");
    }
  };

  const handleRemoveBenefit = (index) => {
    setFormData((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await dispatch(
        createInstallmentPlanThunk({
          branchCode,
          planData: {
            ...formData,
            minOrderValue: Number(formData.minOrderValue),
            maxOrderValue: Number(formData.maxOrderValue),
            tenureMonths: Number(formData.tenureMonths),
            downPayment: {
              ...formData.downPayment,
              minPercentage: Number(formData.downPayment.minPercentage),
              minAmount: Number(formData.downPayment.minAmount) || undefined,
            },
            interestRate: {
              annual: Number(formData.interestRate.annual),
            },
            processingFee: {
              ...formData.processingFee,
              amount: Number(formData.processingFee.amount),
            },
          },
        })
      ).unwrap();

      onSuccess();
    } catch (error) {
      // Errors are handled by validation errors display
      console.error("Failed to create plan:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-2xl font-bold">Create Installment Plan</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-2">
                Please fix the following errors:
              </h3>
              <ul className="list-disc list-inside text-sm text-red-700">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan Name *
              </label>
              <input
                type="text"
                name="planName"
                value={formData.planName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 6 Months No Cost EMI"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenure (Months) *
                </label>
                <input
                  type="number"
                  name="tenureMonths"
                  value={formData.tenureMonths}
                  onChange={handleChange}
                  min="1"
                  max="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Order Value *
                </label>
                <input
                  type="number"
                  name="minOrderValue"
                  value={formData.minOrderValue}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Order Value *
                </label>
                <input
                  type="number"
                  name="maxOrderValue"
                  value={formData.maxOrderValue}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Interest Rate */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Interest Configuration</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Annual Interest Rate (%) *
              </label>
              <input
                type="number"
                name="interestRate.annual"
                value={formData.interestRate.annual}
                onChange={handleChange}
                min="0"
                max="50"
                step="0.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Monthly rate: {(formData.interestRate.annual / 12).toFixed(2)}%
              </p>
            </div>
          </div>

          {/* Down Payment */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Down Payment</h3>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="downPayment.required"
                checked={formData.downPayment.required}
                onChange={handleChange}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Require Down Payment</span>
            </label>

            {formData.downPayment.required && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Percentage (%)
                  </label>
                  <input
                    type="number"
                    name="downPayment.minPercentage"
                    value={formData.downPayment.minPercentage}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Amount (Optional)
                  </label>
                  <input
                    type="number"
                    name="downPayment.minAmount"
                    value={formData.downPayment.minAmount}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Processing Fee */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Processing Fee</h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  name="processingFee.amount"
                  value={formData.processingFee.amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  name="processingFee.type"
                  value={formData.processingFee.type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="PERCENTAGE">Percentage</option>
                  <option value="FIXED">Fixed Amount</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Charged
                </label>
                <select
                  name="processingFee.charged"
                  value={formData.processingFee.charged}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="UPFRONT">Upfront</option>
                  <option value="INCLUDED_IN_EMI">Included in EMI</option>
                </select>
              </div>
            </div>
          </div>

          {/* Special Requirements */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Special Requirements</h3>

            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="requiresPremiumCustomer"
                  checked={formData.requiresPremiumCustomer}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">
                  Requires Premium Customer
                </span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="requiresGuarantor"
                  checked={formData.requiresGuarantor}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">Requires Guarantor</span>
              </label>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Benefits</h3>

            <div className="flex gap-2">
              <input
                type="text"
                value={benefitInput}
                onChange={(e) => setBenefitInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddBenefit();
                  }
                }}
                placeholder="e.g., No prepayment penalty"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddBenefit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
            </div>

            {formData.benefits.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.benefits.map((benefit, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full flex items-center gap-2"
                  >
                    {benefit}
                    <button
                      type="button"
                      onClick={() => handleRemoveBenefit(idx)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Creating..." : "Create Plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInstallmentPlanModal;
